import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSyncAlt, faDownload
} from '@fortawesome/free-solid-svg-icons';
import { Card, Typography, Button, Select, Option, Chip } from "@material-tailwind/react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { exportCategoryStatsToCSV } from '../../../utils/expenseExport';
const { rootUrl } = require('../../../config/config');

const CategoryAnalytics = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('by-category');
    const [categoryStats, setCategoryStats] = useState([]);
    const [subcategoryStats, setSubcategoryStats] = useState([]);
    const [summaryStats, setSummaryStats] = useState(null);
    // Filters
    const [selectedCategory, setSelectedCategory] = useState('');
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        expense_type: ''
    });

    const COLORS = [
        '#FF5733', '#33C3FF', '#33FF57', '#FFD133', '#D133FF', 
        '#FF3380', '#33FFD1', '#FF8C33', '#8C33FF', '#33FF8C',
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchAnalyticsData();
    }, [filters]);

    const fetchAnalyticsData = async () => {
        setLoading(true);
        setError('');
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('year', filters.year);
            if (filters.expense_type) {
                queryParams.append('expense_type', filters.expense_type);
            }

            const response = await axios.get(
                `${rootUrl}/api/expenses/reports/category-analytics?${queryParams}`,
                {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                }
            );

            if (response.data.success) {
                setCategoryStats(response.data.data.categories || []);
                setSubcategoryStats(response.data.data.subcategories || []);
                setSummaryStats(response.data.data.summary || {});
                setSelectedCategory('');
            } else {
                setError('Failed to load analytics data');
            }
        } catch (error) {
            console.error('Error fetching analytics data:', error);
            setError(error.response?.data?.message || 'Error loading analytics data');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const getTotalSpent = () => {
        return summaryStats?.total_amount || 0;
    };

    const getPercentageOfTotal = (amount) => {
        const total = getTotalSpent();
        return total > 0 ? ((parseFloat(amount) / total) * 100).toFixed(1) : 0;
    };

    const getSubcategoriesForCategory = (categoryId) => {
        return subcategoryStats.filter(sub => sub.category_id === categoryId);
    };

    // Get top categories
    const topCategories = categoryStats.slice(0, 10);

    // Prepare data for pie chart
    const pieChartData = categoryStats.map(cat => ({
        name: cat.category_name,
        value: parseFloat(cat.total_amount) || 0,
        category_id: cat.category_id
    }));

    // Group subcategories by category for grouped view
    const groupedByCategory = categoryStats.map(cat => ({
        ...cat,
        subcategories: getSubcategoriesForCategory(cat.category_id)
    }));

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-4">
            {/* Header */}
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Expense Analytics</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Comprehensive analysis of expenses by category and sub-category
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Filters */}
                <Card className="mb-6 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Select
                            label="Year"
                            value={filters.year.toString()}
                            onChange={(val) => setFilters({ ...filters, year: parseInt(val) })}
                        >
                            {[...Array(5)].map((_, i) => {
                                const year = new Date().getFullYear() - i;
                                return (
                                    <Option key={year} value={year.toString()}>
                                        {year}
                                    </Option>
                                );
                            })}
                        </Select>

                        <Select
                            label="Expense Type"
                            value={filters.expense_type}
                            onChange={(val) => setFilters({ ...filters, expense_type: val })}
                        >
                            <Option value="">All Types</Option>
                            <Option value="VEHICLE">Vehicle</Option>
                            <Option value="OFFICE">Office</Option>
                            <Option value="DRIVER">Driver</Option>
                        </Select>

                        <Button
                            onClick={fetchAnalyticsData}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 shadow-none flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} />
                            Refresh
                        </Button>

                        <Button
                            onClick={() => exportCategoryStatsToCSV(categoryStats)}
                            variant="outlined"
                            className="flex items-center justify-center gap-2 rounded-sm"
                            disabled={categoryStats.length === 0}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            Export CSV
                        </Button>
                    </div>
                </Card>

                {/* Summary Stats */}
                {summaryStats && !loading && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <Card className="p-6">
                            <Typography variant="small" className="text-gray-600 font-medium">
                                Total Expenses
                            </Typography>
                            <Typography variant="h4" className="text-[#FF5733] font-bold mt-2">
                                {formatCurrency(getTotalSpent())}
                            </Typography>
                            <Typography variant="small" className="text-gray-500 mt-1">
                                {summaryStats.total_categories || 0} categories
                            </Typography>
                        </Card>

                        <Card className="p-6">
                            <Typography variant="small" className="text-gray-600 font-medium">
                                Total Transactions
                            </Typography>
                            <Typography variant="h4" className="text-blue-600 font-bold mt-2">
                                {summaryStats.vehicle_expenses + summaryStats.office_expenses + summaryStats.driver_expenses}
                            </Typography>
                            <Typography variant="small" className="text-gray-500 mt-1">
                                {summaryStats.total_subcategories || 0} subcategories
                            </Typography>
                        </Card>

                        <Card className="p-6">
                            <Typography variant="small" className="text-gray-600 font-medium">
                                Average Expense
                            </Typography>
                            <Typography variant="h4" className="text-green-600 font-bold mt-2">
                                {formatCurrency(getTotalSpent() / (summaryStats.vehicle_expenses + summaryStats.office_expenses + summaryStats.driver_expenses || 1))}
                            </Typography>
                            <Typography variant="small" className="text-gray-500 mt-1">
                                Per transaction
                            </Typography>
                        </Card>

                        <Card className="p-6">
                            <Typography variant="small" className="text-gray-600 font-medium">
                                Max Expense
                            </Typography>
                            <Typography variant="h4" className="text-purple-600 font-bold mt-2">
                                {formatCurrency(summaryStats.max_expense)}
                            </Typography>
                            <Typography variant="small" className="text-gray-500 mt-1">
                                Highest transaction
                            </Typography>
                        </Card>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <Card className="mb-6 p-4 border border-red-300 bg-red-50">
                        <Typography className="text-red-600 font-semibold">
                            {error}
                        </Typography>
                    </Card>
                )}

                {/* Loading State */}
                {loading ? (
                    <Card className="p-8 text-center">
                        <FontAwesomeIcon icon={faSyncAlt} spin size="3x" className="text-[#FF5733] mb-4" />
                        <Typography>Loading analytics...</Typography>
                    </Card>
                ) : (
                    <>
                        {/* Tab Navigation */}
                        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                            <div className="flex flex-wrap border-b border-gray-200 mb-6">
                                <button
                                    onClick={() => setActiveTab('by-category')}
                                    className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'by-category' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                                >
                                    By Category
                                </button>
                                <button
                                    onClick={() => setActiveTab('by-subcategory')}
                                    className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'by-subcategory' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                                >
                                    By Subcategory
                                </button>
                                <button
                                    onClick={() => setActiveTab('grouped')}
                                    className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'grouped' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                                >
                                    Category & Subcategory
                                </button>
                            </div>

                            {/* By Category Tab */}
                            {activeTab === 'by-category' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Pie Chart */}
                                        <Card className="p-6">
                                            <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                Category Distribution
                                            </Typography>
                                            {pieChartData.length > 0 ? (
                                                <ResponsiveContainer width="100%" height={350}>
                                                    <PieChart>
                                                        <Pie
                                                            data={pieChartData}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                                            outerRadius={100}
                                                            fill="#8884d8"
                                                            dataKey="value"
                                                        >
                                                            {pieChartData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Typography className="text-center text-gray-600 py-8">
                                                    No data available
                                                </Typography>
                                            )}
                                        </Card>

                                        {/* Bar Chart */}
                                        <Card className="p-6">
                                            <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                Top 10 Categories by Amount
                                            </Typography>
                                            {topCategories.length > 0 ? (
                                                <ResponsiveContainer width="100%" height={350}>
                                                    <BarChart data={topCategories}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="category_name" angle={-45} textAnchor="end" height={100} />
                                                        <YAxis />
                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                        <Bar dataKey="total_amount" fill="#FF5733" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Typography className="text-center text-gray-600 py-8">
                                                    No data available
                                                </Typography>
                                            )}
                                        </Card>
                                    </div>

                                    {/* Categories Table */}
                                    {categoryStats.length > 0 ? (
                                        <Card className="overflow-x-auto">
                                            <table className="w-full table-auto">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="p-4 text-left">Category</th>
                                                        <th className="p-4 text-left">Type</th>
                                                        <th className="p-4 text-right">Total Amount</th>
                                                        <th className="p-4 text-right">% of Total</th>
                                                        <th className="p-4 text-right">Count</th>
                                                        <th className="p-4 text-right">Avg Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {categoryStats.map((category, index) => (
                                                        <tr key={category.category_id} className="border-b hover:bg-gray-50">
                                                            <td className="p-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div
                                                                        className="w-4 h-4 rounded-full"
                                                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                                                    />
                                                                    <Typography variant="small" className="font-semibold">
                                                                        {category.category_name}
                                                                    </Typography>
                                                                </div>
                                                            </td>
                                                            <td className="p-4">
                                                                <Chip
                                                                    value={category.expense_type}
                                                                    size="sm"
                                                                    className="bg-blue-100 text-blue-700"
                                                                />
                                                            </td>
                                                            <td className="p-4 text-right font-bold text-[#FF5733]">
                                                                {formatCurrency(category.total_amount)}
                                                            </td>
                                                            <td className="p-4 text-right">
                                                                <Typography variant="small" className="font-semibold">
                                                                    {getPercentageOfTotal(category.total_amount)}%
                                                                </Typography>
                                                            </td>
                                                            <td className="p-4 text-right">
                                                                <Typography variant="small">
                                                                    {category.expense_count}
                                                                </Typography>
                                                            </td>
                                                            <td className="p-4 text-right">
                                                                <Typography variant="small">
                                                                    {formatCurrency(category.avg_amount)}
                                                                </Typography>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Card>
                                    ) : (
                                        <Card className="p-8 text-center">
                                            <Typography className="text-gray-600">No category data available</Typography>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* By Subcategory Tab */}
                            {activeTab === 'by-subcategory' && (
                                <div className="space-y-6">
                                    {subcategoryStats.length > 0 ? (
                                        <>
                                            <Card className="p-6">
                                                <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                    Top 15 Subcategories
                                                </Typography>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <BarChart data={subcategoryStats.slice(0, 15)}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="sub_category_name" angle={-45} textAnchor="end" height={100} />
                                                        <YAxis />
                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                        <Bar dataKey="total_amount" fill="#33C3FF" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </Card>

                                            <Card className="overflow-x-auto">
                                                <table className="w-full table-auto">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="p-4 text-left">Category</th>
                                                            <th className="p-4 text-left">Subcategory</th>
                                                            <th className="p-4 text-right">Total Amount</th>
                                                            <th className="p-4 text-right">% of Total</th>
                                                            <th className="p-4 text-right">Count</th>
                                                            <th className="p-4 text-right">Avg Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {subcategoryStats.map((sub, index) => (
                                                            <tr key={`${sub.category_id}-${sub.sub_category_id}`} className="border-b hover:bg-gray-50">
                                                                <td className="p-4">
                                                                    <Typography variant="small" className="font-semibold">
                                                                        {sub.category_name || 'Uncategorized'}
                                                                    </Typography>
                                                                </td>
                                                                <td className="p-4">
                                                                    <Typography variant="small">
                                                                        {sub.sub_category_name || 'No Subcategory'}
                                                                    </Typography>
                                                                </td>
                                                                <td className="p-4 text-right font-bold text-[#FF5733]">
                                                                    {formatCurrency(sub.total_amount)}
                                                                </td>
                                                                <td className="p-4 text-right">
                                                                    <Typography variant="small">
                                                                        {sub.percentage || 0}%
                                                                    </Typography>
                                                                </td>
                                                                <td className="p-4 text-right">
                                                                    <Typography variant="small">
                                                                        {sub.expense_count}
                                                                    </Typography>
                                                                </td>
                                                                <td className="p-4 text-right">
                                                                    <Typography variant="small">
                                                                        {formatCurrency(sub.avg_amount)}
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </Card>
                                        </>
                                    ) : (
                                        <Card className="p-8 text-center">
                                            <Typography className="text-gray-600">No subcategory data available</Typography>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Grouped Tab */}
                            {activeTab === 'grouped' && (
                                <div className="space-y-6">
                                    {groupedByCategory.length > 0 ? (
                                        groupedByCategory.map((category, catIndex) => (
                                            <Card key={category.category_id} className="p-6">
                                                <div className="mb-6">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className="w-6 h-6 rounded-full"
                                                                style={{ backgroundColor: COLORS[catIndex % COLORS.length] }}
                                                            />
                                                            <Typography variant="h6" className="text-gray-800 font-bold">
                                                                {category.category_name}
                                                            </Typography>
                                                            <Chip
                                                                value={category.expense_type}
                                                                size="sm"
                                                                className="bg-blue-100 text-blue-700"
                                                            />
                                                        </div>
                                                        <div className="text-right">
                                                            <Typography variant="h5" className="text-[#FF5733] font-bold">
                                                                {formatCurrency(category.total_amount)}
                                                            </Typography>
                                                            <Typography variant="small" className="text-gray-600">
                                                                {category.expense_count} transactions
                                                            </Typography>
                                                        </div>
                                                    </div>

                                                    {category.subcategories && category.subcategories.length > 0 ? (
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full table-auto">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="p-3 text-left text-sm">Subcategory</th>
                                                                        <th className="p-3 text-right text-sm">Amount</th>
                                                                        <th className="p-3 text-right text-sm">% of Category</th>
                                                                        <th className="p-3 text-right text-sm">Count</th>
                                                                        <th className="p-3 text-right text-sm">Avg</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {category.subcategories.map((sub, subIndex) => (
                                                                        <tr key={sub.sub_category_id} className="border-b hover:bg-gray-50">
                                                                            <td className="p-3">
                                                                                <Typography variant="small" className="font-semibold">
                                                                                    {sub.sub_category_name || 'No Subcategory'}
                                                                                </Typography>
                                                                            </td>
                                                                            <td className="p-3 text-right font-bold text-[#FF5733]">
                                                                                {formatCurrency(sub.total_amount)}
                                                                            </td>
                                                                            <td className="p-3 text-right">
                                                                                <Typography variant="small">
                                                                                    {((parseFloat(sub.total_amount) / parseFloat(category.total_amount)) * 100).toFixed(1)}%
                                                                                </Typography>
                                                                            </td>
                                                                            <td className="p-3 text-right">
                                                                                <Typography variant="small">
                                                                                    {sub.expense_count}
                                                                                </Typography>
                                                                            </td>
                                                                            <td className="p-3 text-right">
                                                                                <Typography variant="small">
                                                                                    {formatCurrency(sub.avg_amount)}
                                                                                </Typography>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <Typography variant="small" className="text-gray-500 italic">
                                                            No subcategories defined
                                                        </Typography>
                                                    )}
                                                </div>
                                            </Card>
                                        ))
                                    ) : (
                                        <Card className="p-8 text-center">
                                            <Typography className="text-gray-600">No data available</Typography>
                                        </Card>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CategoryAnalytics;

